import React from "react";
import { useTranslation } from "react-i18next";

import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { AddonFeatureTile } from "@dashboard/shared/limits/FeatureTile";
import { FeatureTilesGrid } from "@dashboard/shared/limits/FeatureTilesGrid";
import { addonFeatures, getFeature } from "@dashboard/shared/limits/utils";
import { SectionHeader } from "@dashboard/shared/styles";
import { Chip } from "@mui/material";
import { SectionField, SectionFields } from "@shared/SectionPanel/SectionField";

type Props = {
    features: API.Feature[];
    editable?: boolean;
    onToggle?: (limitType: string, newValue: number) => void;
    variant?: "section" | "pane" | "bare";
};

function AddonStatusList(props: { features: API.Feature[] }) {
    const { t } = useTranslation(["common"]);

    return (
        <SectionFields>
            {addonFeatures.map((limitType) => {
                const enabled = Boolean(getFeature(limitType, props.features).used);

                return (
                    <SectionField
                        key={limitType}
                        label={t(`common:limits.${limitType}`)}
                        value={
                            <Chip
                                label={enabled ? t("common:label.enabled") : t("common:label.disabled")}
                                color={enabled ? "success" : "default"}
                                size="small"
                            />
                        }
                    />
                );
            })}
        </SectionFields>
    );
}

export const SubscriptionAddons = (props: Props) => {
    const { t } = useTranslation(["pages", "common"]);
    const { features, editable = false, onToggle, variant = "section" } = props;

    const deviceManagement = getFeature("DeviceManagement", features);
    const clientManagement = getFeature("ClientManagement", features);
    const productManagement = getFeature("ProductManagement", features);
    const supportService = getFeature("SupportService", features);
    const licenseRevocation = getFeature("LicenseRevocation", features);

    const header = t("common:subscription.addons");

    if (variant === "bare") {
        return <AddonStatusList features={features} />;
    }

    const Grid = (
        <FeatureTilesGrid>
            {deviceManagement ? (
                <AddonFeatureTile
                    limitType="DeviceManagement"
                    title={t("common:limits.DeviceManagement")}
                    features={features}
                    onToggle={onToggle}
                    editable={editable}
                />
            ) : null}
            {productManagement ? (
                <AddonFeatureTile
                    limitType="ProductManagement"
                    title={t("common:limits.ProductManagement")}
                    features={features}
                    onToggle={onToggle}
                    editable={editable}
                />
            ) : null}
            {licenseRevocation ? (
                <AddonFeatureTile
                    limitType="LicenseRevocation"
                    title={t("common:limits.LicenseRevocation")}
                    features={features}
                    onToggle={onToggle}
                    editable={editable}
                />
            ) : null}
            {supportService ? (
                <AddonFeatureTile
                    limitType="SupportService"
                    title={t("common:limits.SupportService")}
                    features={features}
                    onToggle={onToggle}
                    editable={editable}
                />
            ) : null}
            {clientManagement ? (
                <AddonFeatureTile
                    limitType="ClientManagement"
                    title={t("common:limits.ClientManagement")}
                    features={features}
                    onToggle={onToggle}
                    editable={editable}
                />
            ) : null}
        </FeatureTilesGrid>
    );

    if (variant === "pane") {
        return (
            <>
                <PaneElement.Header>{header}</PaneElement.Header>
                {Grid}
            </>
        );
    }

    return (
        <>
            <SectionHeader>{header}</SectionHeader>
            {Grid}
        </>
    );
};
